import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Trash2, Edit, Plus, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Authentication hook
const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });

  const login = async (username, password) => {
    try {
      // Validation côté client d'abord
      if (!username || !password) {
        console.log('Login failed: empty credentials');
        return false;
      }
      
      // Test avec l'endpoint de test d'authentification
      const response = await axios.get(`${API}/admin/test-auth`, {
        auth: { username, password },
        timeout: 10000 // 10 secondes de timeout
      });
      
      if (response.status === 200 && response.data.message) {
        console.log('Login successful:', response.data.message);
        setCredentials({ username, password });
        setIsAuthenticated(true);
        return true;
      }
      
      console.log('Login failed: unexpected response');
      return false;
      
    } catch (error) {
      console.log('Login failed:', error.response?.status, error.response?.data?.detail);
      
      // Reset de l'état en cas d'erreur
      setCredentials({ username: '', password: '' });
      setIsAuthenticated(false);
      
      return false;
    }
  };

  const logout = () => {
    setCredentials({ username: '', password: '' });
    setIsAuthenticated(false);
  };

  // Vérification périodique de l'authentification
  const checkAuth = async () => {
    if (!isAuthenticated || !credentials.username) {
      return false;
    }
    
    try {
      await axios.get(`${API}/admin/test-auth`, {
        auth: credentials,
        timeout: 5000
      });
      return true;
    } catch (error) {
      console.log('Auth check failed, logging out');
      logout();
      return false;
    }
  };

  return { isAuthenticated, credentials, login, logout, checkAuth };
};

// Create authenticated axios instance
const createAuthAxios = (credentials) => {
  return axios.create({
    auth: credentials
  });
};

const Admin = () => {
  const { isAuthenticated, credentials, login } = useAuth();
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');

  // State for different sections
  const [carouselImages, setCarouselImages] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [realizations, setRealizations] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [keyMetrics, setKeyMetrics] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);

  // Form states
  const [newCarouselImage, setNewCarouselImage] = useState({ title: '', description: '', image_url: '' });
  const [newEquipment, setNewEquipment] = useState({ name: '', category: '', description: '', image_url: '', specifications: '' });
  const [newRealization, setNewRealization] = useState({ 
    title: '', 
    description: '', 
    image_urls: ['', '', ''],  // 3 champs pour les images
    date: '' 
  });
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });

  // Clients states
  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState({ 
    name: '', 
    logo_url: '', 
    website: '' 
  });
  const [editingClient, setEditingClient] = useState(null);
  const [isEditModeClient, setIsEditModeClient] = useState(false);

  // Edit states
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRealization, setEditingRealization] = useState(null);
  const [isEditModeRealization, setIsEditModeRealization] = useState(false);
  const [editingCarousel, setEditingCarousel] = useState(null);
  const [isEditModeCarousel, setIsEditModeCarousel] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [isEditModeFaq, setIsEditModeFaq] = useState(false);

  const authAxios = isAuthenticated ? createAuthAxios(credentials) : null;

const handleLogin = async (e) => {
  e.preventDefault();
  setLoginError('');
  
  console.log('Form login attempt:', loginForm.username);
  
    // Validation côté client
    if (!loginForm.username || !loginForm.password) {
      setLoginError('Veuillez saisir un nom d\'utilisateur et un mot de passe');
      return;
    }
  
    try {
      const success = await login(loginForm.username, loginForm.password);
    
      if (!success) {
        setLoginError('Nom d\'utilisateur ou mot de passe incorrect');
        // Vider les champs après un échec
        setLoginForm({ username: '', password: '' });
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Erreur de connexion. Veuillez réessayer.');
      setLoginForm({ username: '', password: '' });
    }
  };

  // Upload functions
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await authAxios.post(`${API}/admin/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.image_url;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const handleImageUpload = async (event, setImageUrl) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const imageUrl = await uploadImage(file);
        setImageUrl(imageUrl);
        alert('Image uploadée avec succès !');
      } catch (error) {
        alert('Erreur lors de l\'upload de l\'image');
      }
    }
  };

  const [installations, setInstallations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [newInstallations, setNewInstallations] = useState({ 
    title: '', 
    description: '', 
    location: '', 
    image_url: '', 
    category: 'Restaurant' 
  });

  const [editingInstallations, setEditingInstallations] = useState(null);
  const [isEditModeInstallations, setIsEditModeInstallations] = useState(false);

  const [newBrand, setNewBrand] = useState({ 
    name: '', 
    logo_url: '', 
    website: '' 
  });

  // INSTALLATION FUNCTIONS
  const addInstallations = async () => {
    try {
      await authAxios.post(`${API}/admin/installations`, newInstallations);
      setNewInstallations({ title: '', description: '', location: '', image_url: '', category: 'Bar & Restaurant' });
      fetchInstallations();
      alert('Installation ajoutée avec succès !');
    } catch (error) {
      console.error('Error adding installation:', error);
    }
  }; 

  const editInstallations = (installations) => {
    setEditingInstallations(installations);
    setNewInstallations({
      id: installations.id,
      title: installations.title,
      description: installations.description,
      location: installations.location,
      image_url: installations.image_url,
      category: installations.category
    });
    setIsEditModeInstallations(true);
    // Scroll vers le formulaire
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateInstallations = async () => {
    try {
      await authAxios.put(`${API}/admin/installations/${newInstallations.id}`, newInstallations);
      setNewInstallations({ 
        title: '', 
        description: '', 
        location: '', 
        image_url: '', 
        category: 'Restaurant' 
      });
      setEditingInstallations(null);
      setIsEditModeInstallations(false);
      fetchInstallations();
      alert('Installation mise à jour avec succès !');
    } catch (error) {
      console.error('Error updating installation:', error);
      alert('Erreur lors de la mise à jour de l\'installation');
    }
  };

  const cancelEditInstallations = () => {
    setNewInstallations({ 
      title: '', 
      description: '', 
      location: '', 
      image_url: '', 
      category: 'Restaurant' 
    });
    setEditingInstallations(null);
    setIsEditModeInstallations(false);
  };

  const deleteInstallations = async (id) => {
    if (window.confirm('Supprimer cette installation ?')) {
      try {
        await authAxios.delete(`${API}/admin/installations/${id}`);
        fetchInstallations();
      } catch (error) {
        console.error('Error deleting installation:', error);
      }
    }
  };

  // BRAND FUNCTIONS
  const addBrand = async () => {
    try {
      await authAxios.post(`${API}/admin/brands`, newBrand);
      setNewBrand({ name: '', logo_url: '', website: '' });
      fetchBrands();
      alert('Marque ajoutée avec succès !');
    } catch (error) {
      console.error('Error adding brand:', error);
    }
  };

  const deleteBrand = async (id) => {
    if (window.confirm('Supprimer cette marque ?')) {
      try {
        await authAxios.delete(`${API}/admin/brands/${id}`);
        fetchBrands();
      } catch (error) {
        console.error('Error deleting brand:', error);
      }
    }
  };

  // Fetch data functions
  const fetchCarouselImages = async () => {
    try {
      const response = await axios.get(`${API}/carousel`);
      setCarouselImages(response.data);
    } catch (error) {
      console.error('Error fetching carousel images:', error);
    }
  };

  const fetchEquipment = async () => {
    try {
      const response = await axios.get(`${API}/equipment`);
      setEquipment(response.data);
    } catch (error) {
      console.error('Error fetching equipment:', error);
    }
  };

  const fetchRealizations = async () => {
    try {
      const response = await axios.get(`${API}/realizations`);
      setRealizations(response.data);
    } catch (error) {
      console.error('Error fetching realizations:', error);
    }
  };

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const fetchKeyMetrics = async () => {
    try {
      const response = await axios.get(`${API}/key-metrics`);
      setKeyMetrics(response.data);
    } catch (error) {
      console.error('Error fetching key metrics:', error);
    }
  };

  const fetchContactInfo = async () => {
    try {
      const response = await axios.get(`${API}/contact-info`);
      setContactInfo(response.data);
    } catch (error) {
      console.error('Error fetching contact info:', error);
    }
  };

  const fetchInstallations = async () => {
    try {
      const response = await axios.get(`${API}/installations`);
      setInstallations(response.data);
    } catch (error) {
      console.error('Error fetching installations:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

useEffect(() => {
  if (isAuthenticated) {
    // Vérifier que l'authentification est toujours valide
    const verifyAuth = async () => {
      try {
        await axios.get(`${API}/admin/test-auth`, {
          auth: credentials,
          timeout: 5000
        });
        // Si succès, charger les données
        fetchCarouselImages();
        fetchEquipment();
        fetchRealizations();
        fetchFaqs();
        fetchKeyMetrics();
        fetchContactInfo();
        fetchInstallations();
        fetchBrands();
        fetchClients();
      } catch (error) {
        console.log('Auth verification failed, redirecting to login');
        setIsAuthenticated(false);
        setCredentials({ username: '', password: '' });
      }
    };
    
    verifyAuth();
  }
}, [isAuthenticated]);

  // CAROUSEL FUNCTIONS
  const addCarouselImage = async () => {
    try {
      await authAxios.post(`${API}/admin/carousel`, newCarouselImage);
      setNewCarouselImage({ title: '', description: '', image_url: '' });
      fetchCarouselImages();
    } catch (error) {
      console.error('Error adding carousel image:', error);
    }
  };

  const editCarouselImage = (image) => {
    setEditingCarousel(image);
    setNewCarouselImage({
      id: image.id,
      title: image.title,
      description: image.description,
      image_url: image.image_url
    });
    setIsEditModeCarousel(true);
  };

  const updateCarouselImage = async () => {
    try {
      await authAxios.put(`${API}/admin/carousel/${newCarouselImage.id}`, newCarouselImage);
      setNewCarouselImage({ title: '', description: '', image_url: '' });
      setEditingCarousel(null);
      setIsEditModeCarousel(false);
      fetchCarouselImages();
      alert('Image de carrousel mise à jour avec succès !');
    } catch (error) {
      console.error('Error updating carousel image:', error);
      alert('Erreur lors de la mise à jour de l\'image');
    }
  };

  const cancelEditCarousel = () => {
    setNewCarouselImage({ title: '', description: '', image_url: '' });
    setEditingCarousel(null);
    setIsEditModeCarousel(false);
  };

  const deleteCarouselImage = async (id) => {
    try {
      await authAxios.delete(`${API}/admin/carousel/${id}`);
      fetchCarouselImages();
    } catch (error) {
      console.error('Error deleting carousel image:', error);
    }
  };

  // CLIENT FUNCTIONS
  const addClient = async () => {
    try {
      await authAxios.post(`${API}/admin/clients`, newClient);
      setNewClient({ name: '', logo_url: '', website: '' });
      fetchClients();
      alert('Client ajouté avec succès !');
    } catch (error) {
      console.error('Error adding client:', error);
    }
  };

  const editClient = (client) => {
    setEditingClient(client);
    setNewClient({
      id: client.id,
      name: client.name,
      logo_url: client.logo_url,
      website: client.website || ''
    });
    setIsEditModeClient(true);
    // Scroll vers le formulaire
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateClient = async () => {
    try {
      await authAxios.put(`${API}/admin/clients/${newClient.id}`, newClient);
      setNewClient({ name: '', logo_url: '', website: '' });
      setEditingClient(null);
      setIsEditModeClient(false);
      fetchClients();
      alert('Client mis à jour avec succès !');
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Erreur lors de la mise à jour du client');
    }
  };

  const cancelEditClient = () => {
    setNewClient({ name: '', logo_url: '', website: '' });
    setEditingClient(null);
    setIsEditModeClient(false);
  };

  const deleteClient = async (id) => {
    if (window.confirm('Supprimer ce client ?')) {
      try {
        await authAxios.delete(`${API}/admin/clients/${id}`);
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
      }
    }
  };

  // EQUIPMENT FUNCTIONS
  const addEquipment = async () => {
    try {
      await authAxios.post(`${API}/admin/equipment`, newEquipment);
      setNewEquipment({ name: '', category: '', description: '', image_url: '', specifications: '' });
      fetchEquipment();
    } catch (error) {
      console.error('Error adding equipment:', error);
    }
  };

  const editEquipment = (equipment) => {
    setEditingEquipment(equipment);
    setNewEquipment({
      id: equipment.id,
      name: equipment.name,
      category: equipment.category,
      description: equipment.description,
      image_url: equipment.image_url,
      specifications: equipment.specifications || ''
    });
    setIsEditMode(true);
  };

  const updateEquipment = async () => {
    try {
      await authAxios.put(`${API}/admin/equipment/${newEquipment.id}`, newEquipment);
      setNewEquipment({ name: '', category: '', description: '', image_url: '', specifications: '' });
      setEditingEquipment(null);
      setIsEditMode(false);
      fetchEquipment();
      alert('Équipement mis à jour avec succès !');
    } catch (error) {
      console.error('Error updating equipment:', error);
      alert('Erreur lors de la mise à jour de l\'équipement');
    }
  };

  const cancelEdit = () => {
    setNewEquipment({ name: '', category: '', description: '', image_url: '', specifications: '' });
    setEditingEquipment(null);
    setIsEditMode(false);
  };

  const deleteEquipment = async (id) => {
    try {
      await authAxios.delete(`${API}/admin/equipment/${id}`);
      fetchEquipment();
    } catch (error) {
      console.error('Error deleting equipment:', error);
    }
  };

  // REALIZATION FUNCTIONS
  const addRealization = async () => {
    try {
      // Filtrer les URLs vides avant d'envoyer
      const realizationData = {
        ...newRealization,
        image_urls: newRealization.image_urls.filter(url => url.trim() !== '')
      };
      await authAxios.post(`${API}/admin/realizations`, realizationData);
      setNewRealization({ 
        title: '', 
        description: '', 
        image_urls: ['', '', ''],  // ✅ CORRECTION ICI
        date: '' 
      });
      fetchRealizations();
    } catch (error) {
      console.error('Error adding realization:', error);
    }
  };


  const editRealization = (realization) => {
    setEditingRealization(realization);
    // Assurer que nous avons toujours 3 emplacements
    const imageUrls = realization.image_urls || [realization.image_url || ''];
    const paddedUrls = [...imageUrls, '', '', ''].slice(0, 3);
    
    setNewRealization({
      id: realization.id,
      title: realization.title,
      description: realization.description,
      image_urls: paddedUrls,
      date: realization.date
    });
    setIsEditModeRealization(true);
  };

  const updateRealization = async () => {
    try {
      // Filtrer les URLs vides avant d'envoyer
      const realizationData = {
        ...newRealization,
        image_urls: newRealization.image_urls.filter(url => url.trim() !== '')
      };
      await authAxios.put(`${API}/admin/realizations/${newRealization.id}`, realizationData);
      setNewRealization({ 
        title: '', 
        description: '', 
        image_urls: ['', '', ''], 
        date: '' 
      });
      setEditingRealization(null);
      setIsEditModeRealization(false);
      fetchRealizations();
      alert('Réalisation mise à jour avec succès !');
    } catch (error) {
      console.error('Error updating realization:', error);
      alert('Erreur lors de la mise à jour de la réalisation');
    }
  };

  const cancelEditRealization = () => {
    setNewRealization({ 
      title: '', 
      description: '', 
      image_urls: ['', '', ''], 
      date: '' 
    });
    setEditingRealization(null);
    setIsEditModeRealization(false);
  };

  const deleteRealization = async (id) => {
    try {
      await authAxios.delete(`${API}/admin/realizations/${id}`);
      fetchRealizations();
    } catch (error) {
      console.error('Error deleting realization:', error);
    }
  };

  // FAQ FUNCTIONS
  const addFaq = async () => {
    try {
      await authAxios.post(`${API}/admin/faq`, newFaq);
      setNewFaq({ question: '', answer: '' });
      fetchFaqs();
    } catch (error) {
      console.error('Error adding FAQ:', error);
    }
  };

  const editFaq = (faq) => {
    setEditingFaq(faq);
    setNewFaq({
      id: faq.id,
      question: faq.question,
      answer: faq.answer
    });
    setIsEditModeFaq(true);
  };

  const updateFaq = async () => {
    try {
      await authAxios.put(`${API}/admin/faq/${newFaq.id}`, newFaq);
      setNewFaq({ question: '', answer: '' });
      setEditingFaq(null);
      setIsEditModeFaq(false);
      fetchFaqs();
      alert('FAQ mise à jour avec succès !');
    } catch (error) {
      console.error('Error updating FAQ:', error);
      alert('Erreur lors de la mise à jour de la FAQ');
    }
  };

  const cancelEditFaq = () => {
    setNewFaq({ question: '', answer: '' });
    setEditingFaq(null);
    setIsEditModeFaq(false);
  };

  const deleteFaq = async (id) => {
    try {
      await authAxios.delete(`${API}/admin/faq/${id}`);
      fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
    }
  };

  // OTHER FUNCTIONS
  const updateKeyMetrics = async () => {
    try {
      await authAxios.put(`${API}/admin/key-metrics`, keyMetrics);
      alert('Chiffres clés mis à jour avec succès !');
    } catch (error) {
      console.error('Error updating key metrics:', error);
    }
  };

  const updateContactInfo = async () => {
    try {
      await authAxios.put(`${API}/admin/contact-info`, contactInfo);
      alert('Informations de contact mises à jour avec succès !');
    } catch (error) {
      console.error('Error updating contact info:', error);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">Panel d'Administration - TEKSAS</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom d'utilisateur
                </label>
                <Input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe
                </label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                />
              </div>
              {loginError && (
                <p className="text-red-600 text-sm">{loginError}</p>
              )}
              <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600">
                Se connecter
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Panel d'Administration TEKSAS</h1>
          <p className="text-gray-600">Gérez le contenu de votre site web</p>
        </div>

        <Tabs defaultValue="carousel" className="space-y-4">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="carousel">Carrousel</TabsTrigger>
            <TabsTrigger value="equipment">Équipements</TabsTrigger>
            <TabsTrigger value="realizations">Réalisations</TabsTrigger>
            <TabsTrigger value="faq">FAQ</TabsTrigger>
            <TabsTrigger value="metrics">Chiffres Clés</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="installations">Installations</TabsTrigger>
            <TabsTrigger value="brands">Marques</TabsTrigger>
            <TabsTrigger value="clients">Clients</TabsTrigger>
          </TabsList>

          {/* Carousel Management */}
          <TabsContent value="carousel">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>
                    {isEditModeCarousel ? 'Modifier l\'image du carrousel' : 'Ajouter une image au carrousel'}
                  </CardTitle>
                  {isEditModeCarousel && (
                    <Button 
                      onClick={cancelEditCarousel} 
                      variant="outline" 
                      size="sm"
                      className="mt-2"
                    >
                      Annuler la modification
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Titre"
                    value={newCarouselImage.title}
                    onChange={(e) => setNewCarouselImage({ ...newCarouselImage, title: e.target.value })}
                  />
                  <Input
                    placeholder="Description"
                    value={newCarouselImage.description}
                    onChange={(e) => setNewCarouselImage({ ...newCarouselImage, description: e.target.value })}
                  />
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Image du carrousel
                    </label>
                    <Input
                      placeholder="URL de l'image (optionnel)"
                      value={newCarouselImage.image_url}
                      onChange={(e) => setNewCarouselImage({ ...newCarouselImage, image_url: e.target.value })}
                    />
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">ou</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, (url) => setNewCarouselImage({ ...newCarouselImage, image_url: url }))}
                        className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                      />
                    </div>
                    {newCarouselImage.image_url && (
                      <img src={newCarouselImage.image_url} alt="Preview" className="w-20 h-20 object-cover rounded" />
                    )}
                  </div>
                  <Button 
                    onClick={isEditModeCarousel ? updateCarouselImage : addCarouselImage} 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    {isEditModeCarousel ? (
                      <>
                        <Edit size={16} className="mr-2" />
                        Mettre à jour
                      </>
                    ) : (
                      <>
                        <Plus size={16} className="mr-2" />
                        Ajouter
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Images du carrousel ({carouselImages.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {carouselImages.map((image) => (
                      <div key={image.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <img src={image.image_url} alt={image.title} className="w-16 h-16 object-cover rounded" />
                          <div>
                            <p className="font-medium">{image.title}</p>
                            <p className="text-sm text-gray-600">{image.description}</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => editCarouselImage(image)}
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteCarouselImage(image.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Equipment Management */}
          <TabsContent value="equipment">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>
                    {isEditMode ? 'Modifier l\'équipement' : 'Ajouter un équipement'}
                  </CardTitle>
                  {isEditMode && (
                    <Button 
                      onClick={cancelEdit} 
                      variant="outline" 
                      size="sm"
                      className="mt-2"
                    >
                      Annuler la modification
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Nom de l'équipement"
                    value={newEquipment.name}
                    onChange={(e) => setNewEquipment({ ...newEquipment, name: e.target.value })}
                  />
                  <Select value={newEquipment.category} onValueChange={(value) => setNewEquipment({ ...newEquipment, category: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sonorisation">Sonorisation</SelectItem>
                      <SelectItem value="Éclairage">Éclairage</SelectItem>
                      <SelectItem value="Vidéo">Vidéo</SelectItem>
                      <SelectItem value="Structure">Structure</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Description"
                    value={newEquipment.description}
                    onChange={(e) => setNewEquipment({ ...newEquipment, description: e.target.value })}
                  />
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Image de l'équipement
                    </label>
                    <Input
                      placeholder="URL de l'image (optionnel)"
                      value={newEquipment.image_url}
                      onChange={(e) => setNewEquipment({ ...newEquipment, image_url: e.target.value })}
                    />
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">ou</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, (url) => setNewEquipment({ ...newEquipment, image_url: url }))}
                        className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                      />
                    </div>
                    {newEquipment.image_url && (
                      <img src={newEquipment.image_url} alt="Preview" className="w-20 h-20 object-cover rounded" />
                    )}
                  </div>
                  <Input
                    placeholder="Spécifications techniques"
                    value={newEquipment.specifications}
                    onChange={(e) => setNewEquipment({ ...newEquipment, specifications: e.target.value })}
                  />
                  <Button 
                    onClick={isEditMode ? updateEquipment : addEquipment} 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    {isEditMode ? (
                      <>
                        <Edit size={16} className="mr-2" />
                        Mettre à jour
                      </>
                    ) : (
                      <>
                        <Plus size={16} className="mr-2" />
                        Ajouter
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Équipements ({equipment.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {equipment.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <img src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded" />
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <Badge variant="secondary">{item.category}</Badge>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => editEquipment(item)}
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteEquipment(item.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Realizations Management */}
          <TabsContent value="realizations">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>
                    {isEditModeRealization ? 'Modifier la réalisation' : 'Ajouter une réalisation'}
                  </CardTitle>
                  {isEditModeRealization && (
                    <Button variant="outline" onClick={cancelEditRealization}>
                      Annuler la modification
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Titre"
                    value={newRealization.title}
                    onChange={(e) => setNewRealization({ ...newRealization, title: e.target.value })}
                  />
                  <Textarea
                    placeholder="Description"
                    value={newRealization.description}
                    onChange={(e) => setNewRealization({ ...newRealization, description: e.target.value })}
                  />
                  
                  {/* Image 1 */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Image 1 (obligatoire)</label>
                    <Input
                      placeholder="URL de l'image 1"
                      value={newRealization.image_urls[0] || ''}
                      onChange={(e) => {
                        const newUrls = [...newRealization.image_urls];
                        newUrls[0] = e.target.value;
                        setNewRealization({ ...newRealization, image_urls: newUrls });
                      }}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, (url) => {
                        const newUrls = [...newRealization.image_urls];
                        newUrls[0] = url;
                        setNewRealization({ ...newRealization, image_urls: newUrls });
                      })}
                      className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                    />
                    {newRealization.image_urls[0] && (
                      <img src={newRealization.image_urls[0]} alt="Preview 1" className="w-20 h-20 object-cover rounded" />
                    )}
                  </div>
                  
                  {/* Image 2 */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Image 2 (optionnelle)</label>
                    <Input
                      placeholder="URL de l'image 2"
                      value={newRealization.image_urls[1] || ''}
                      onChange={(e) => {
                        const newUrls = [...newRealization.image_urls];
                        newUrls[1] = e.target.value;
                        setNewRealization({ ...newRealization, image_urls: newUrls });
                      }}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, (url) => {
                        const newUrls = [...newRealization.image_urls];
                        newUrls[1] = url;
                        setNewRealization({ ...newRealization, image_urls: newUrls });
                      })}
                      className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                    />
                    {newRealization.image_urls[1] && (
                      <img src={newRealization.image_urls[1]} alt="Preview 2" className="w-20 h-20 object-cover rounded" />
                    )}
                  </div>
                  
                  {/* Image 3 */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Image 3 (optionnelle)</label>
                    <Input
                      placeholder="URL de l'image 3"
                      value={newRealization.image_urls[2] || ''}
                      onChange={(e) => {
                        const newUrls = [...newRealization.image_urls];
                        newUrls[2] = e.target.value;
                        setNewRealization({ ...newRealization, image_urls: newUrls });
                      }}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, (url) => {
                        const newUrls = [...newRealization.image_urls];
                        newUrls[2] = url;
                        setNewRealization({ ...newRealization, image_urls: newUrls });
                      })}
                      className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                    />
                    {newRealization.image_urls[2] && (
                      <img src={newRealization.image_urls[2]} alt="Preview 3" className="w-20 h-20 object-cover rounded" />
                    )}
                  </div>
                  
                  <Input
                    type="date"
                    value={newRealization.date}
                    onChange={(e) => setNewRealization({ ...newRealization, date: e.target.value })}
                  />
                  
                  {isEditModeRealization ? (
                    <Button onClick={updateRealization} className="w-full bg-orange-500 hover:bg-orange-600">
                      <Save size={16} className="mr-2" />
                      Mettre à jour
                    </Button>
                  ) : (
                    <Button onClick={addRealization} className="w-full bg-orange-500 hover:bg-orange-600">
                      <Plus size={16} className="mr-2" />
                      Ajouter
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Réalisations ({realizations.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {realizations.map((realization) => (
                      <div key={realization.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <img src={realization.image_url} alt={realization.title} className="w-16 h-16 object-cover rounded" />
                          <div>
                            <p className="font-medium">{realization.title}</p>
                            <p className="text-sm text-gray-600">{realization.date}</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => editRealization(realization)}
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteRealization(realization.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* FAQ Management */}
          <TabsContent value="faq">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>
                    {isEditModeFaq ? 'Modifier la FAQ' : 'Ajouter une FAQ'}
                  </CardTitle>
                  {isEditModeFaq && (
                    <Button 
                      onClick={cancelEditFaq} 
                      variant="outline" 
                      size="sm"
                      className="mt-2"
                    >
                      Annuler la modification
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Question"
                    value={newFaq.question}
                    onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                  />
                  <Textarea
                    placeholder="Réponse"
                    value={newFaq.answer}
                    onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                  />
                  <Button 
                    onClick={isEditModeFaq ? updateFaq : addFaq} 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    {isEditModeFaq ? (
                      <>
                        <Edit size={16} className="mr-2" />
                        Mettre à jour
                      </>
                    ) : (
                      <>
                        <Plus size={16} className="mr-2" />
                        Ajouter
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Questions fréquentes ({faqs.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {faqs.map((faq) => (
                      <div key={faq.id} className="flex items-start justify-between p-3 border rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium">{faq.question}</p>
                          <p className="text-sm text-gray-600 mt-1">{faq.answer}</p>
                        </div>
                        <div className="flex space-x-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => editFaq(faq)}
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteFaq(faq.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Key Metrics Management */}
          <TabsContent value="metrics">
            <Card>
              <CardHeader>
                <CardTitle>Chiffres Clés</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {keyMetrics.map((metric, index) => (
                    <div key={metric.id} className="space-y-2">
                      <Input
                        placeholder="Libellé"
                        value={metric.label}
                        onChange={(e) => {
                          const updated = [...keyMetrics];
                          updated[index].label = e.target.value;
                          setKeyMetrics(updated);
                        }}
                      />
                      <Input
                        placeholder="Valeur"
                        value={metric.value}
                        onChange={(e) => {
                          const updated = [...keyMetrics];
                          updated[index].value = e.target.value;
                          setKeyMetrics(updated);
                        }}
                      />
                    </div>
                  ))}
                </div>
                <Button onClick={updateKeyMetrics} className="bg-orange-500 hover:bg-orange-600">
                  <Save size={16} className="mr-2" />
                  Sauvegarder les chiffres clés
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Info Management */}
          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle>Informations de Contact</CardTitle>
              </CardHeader>
              <CardContent>
                {contactInfo && (
                  <div className="space-y-4">
                    <Input
                      placeholder="Téléphone"
                      value={contactInfo.phone}
                      onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                    />
                    <Input
                      placeholder="Email"
                      value={contactInfo.email}
                      onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                    />
                    <Input
                      placeholder="Adresse"
                      value={contactInfo.address}
                      onChange={(e) => setContactInfo({ ...contactInfo, address: e.target.value })}
                    />
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Image du dépôt
                      </label>
                      <Input
                        placeholder="URL de l'image du dépôt (optionnel)"
                        value={contactInfo.depot_image_url}
                        onChange={(e) => setContactInfo({ ...contactInfo, depot_image_url: e.target.value })}
                      />
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">ou</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, (url) => setContactInfo({ ...contactInfo, depot_image_url: url }))}
                          className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                        />
                      </div>
                      {contactInfo.depot_image_url && (
                        <img src={contactInfo.depot_image_url} alt="Preview" className="w-20 h-20 object-cover rounded" />
                      )}
                    </div>
                    <Button onClick={updateContactInfo} className="bg-orange-500 hover:bg-orange-600">
                      <Save size={16} className="mr-2" />
                      Sauvegarder les informations
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Installations Management */}
          <TabsContent value="installations">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>
                      {isEditModeInstallations ? 'Modifier l\'installation' : 'Ajouter une installation'}
                    </CardTitle>
                    {isEditModeInstallations && (
                      <Button variant="outline" onClick={cancelEditInstallations}>
                        Annuler la modification
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Titre (ex: Restaurant Le Gourmet)"
                    value={newInstallations.title}
                    onChange={(e) => setNewInstallations({ ...newInstallations, title: e.target.value })}
                  />
                  <Textarea
                    placeholder="Description"
                    value={newInstallations.description}
                    onChange={(e) => setNewInstallations({ ...newInstallations, description: e.target.value })}
                  />
                  <Input
                    placeholder="Lieu (ex: Lyon 6ème)"
                    value={newInstallations.location}
                    onChange={(e) => setNewInstallations({ ...newInstallations, location: e.target.value })}
                  />
                  <Select 
                    value={newInstallations.category} 
                    onValueChange={(value) => setNewInstallations({ ...newInstallations, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bar & Restaurant">Bar & Restaurant</SelectItem>
                      <SelectItem value="Salles">Salles</SelectItem>
                      <SelectItem value="Autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Image de l'installation</label>
                    <Input
                      placeholder="URL de l'image"
                      value={newInstallations.image_url}
                      onChange={(e) => setNewInstallations({ ...newInstallations, image_url: e.target.value })}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, (url) => setNewInstallations({ ...newInstallation, image_url: url }))}
                      className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                    />
                    {newInstallations.image_url && (
                      <img src={newInstallations.image_url} alt="Preview" className="w-20 h-20 object-cover rounded" />
                    )}
                  </div>
                  
                  {isEditModeInstallations ? (
                    <Button onClick={updateInstallations} className="w-full bg-orange-500 hover:bg-orange-600">
                      <Save size={16} className="mr-2" />
                      Mettre à jour
                    </Button>
                  ) : (
                    <Button onClick={addInstallations} className="w-full bg-orange-500 hover:bg-orange-600">
                      <Plus size={16} className="mr-2" />
                      Ajouter
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Installations ({installations.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {installations.map((installations) => (
                      <div key={installations.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <img src={installations.image_url} alt={installations.title} className="w-16 h-16 object-cover rounded" />
                          <div>
                            <p className="font-medium">{installations.title}</p>
                            <p className="text-sm text-gray-600">{installations.location}</p>
                            <Badge variant="secondary" className="text-xs">{installations.category}</Badge>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => editInstallations(installations)}
                            className="text-orange-600 hover:text-orange-700"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteInstallations(installations.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Brands Management */}
          <TabsContent value="brands">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Ajouter une marque</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Nom de la marque"
                    value={newBrand.name}
                    onChange={(e) => setNewBrand({ ...newBrand, name: e.target.value })}
                  />
                  <Input
                    placeholder="Site web (optionnel)"
                    value={newBrand.website}
                    onChange={(e) => setNewBrand({ ...newBrand, website: e.target.value })}
                  />
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Logo de la marque</label>
                    <Input
                      placeholder="URL du logo"
                      value={newBrand.logo_url}
                      onChange={(e) => setNewBrand({ ...newBrand, logo_url: e.target.value })}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, (url) => setNewBrand({ ...newBrand, logo_url: url }))}
                      className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                    />
                    {newBrand.logo_url && (
                      <img src={newBrand.logo_url} alt="Preview" className="w-32 h-20 object-contain bg-gray-100 rounded p-2" />
                    )}
                  </div>
                  
                  <Button onClick={addBrand} className="w-full bg-orange-500 hover:bg-orange-600">
                    <Plus size={16} className="mr-2" />
                    Ajouter
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Marques partenaires ({brands.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {brands.map((brand) => (
                      <div key={brand.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <img src={brand.logo_url} alt={brand.name} className="w-16 h-12 object-contain bg-gray-100 rounded p-1" />
                          <div>
                            <p className="font-medium">{brand.name}</p>
                            {brand.website && (
                              <a href={brand.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                                {brand.website}
                              </a>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteBrand(brand.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Clients Management */}
          <TabsContent value="clients">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>
                      {isEditModeClient ? 'Modifier le client' : 'Ajouter un client'}
                    </CardTitle>
                    {isEditModeClient && (
                      <Button variant="outline" onClick={cancelEditClient}>
                        Annuler la modification
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Input
                    placeholder="Nom du client"
                    value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  />
                  <Input
                    placeholder="Site web (optionnel)"
                    value={newClient.website}
                    onChange={(e) => setNewClient({ ...newClient, website: e.target.value })}
                  />
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Logo du client</label>
                    <Input
                      placeholder="URL du logo"
                      value={newClient.logo_url}
                      onChange={(e) => setNewClient({ ...newClient, logo_url: e.target.value })}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, (url) => setNewClient({ ...newClient, logo_url: url }))}
                      className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                    />
                    {newClient.logo_url && (
                      <img src={newClient.logo_url} alt="Preview" className="w-32 h-20 object-contain bg-gray-100 rounded p-2" />
                    )}
                  </div>
                  
                  {isEditModeClient ? (
                    <Button onClick={updateClient} className="w-full bg-orange-500 hover:bg-orange-600">
                      <Save size={16} className="mr-2" />
                      Mettre à jour
                    </Button>
                  ) : (
                    <Button onClick={addClient} className="w-full bg-orange-500 hover:bg-orange-600">
                      <Plus size={16} className="mr-2" />
                      Ajouter
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Clients ({clients.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                  {clients.map((client) => (
                    <div key={client.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <img src={client.logo_url} alt={client.name} className="w-16 h-12 object-contain bg-gray-100 rounded p-1" />
                        <div>
                          <p className="font-medium">{client.name}</p>
                          {client.website && (
                            <a href={client.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                              {client.website}
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => editClient(client)}
                          className="text-orange-600 hover:text-orange-700"
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteClient(client.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
